import type { RefObject } from "react";
import { useEffect, useState } from "react";

export interface IUseInViewportOptions {
    rootSelector?: string;
    rootMargin?: string;
    once?: boolean;
    forceVisible?: boolean;
}

/*
    This hook lets you know if the element referenced by ref is in the viewport.
    `once` keeps the result true after the first intersection (e.g. to defer a fetch until first seen).
    `rootSelector` observes against the closest matching ancestor (e.g. the page scroll container) instead of
    the browser viewport, which `rootMargin` needs when the page scrolls inside a nested container.
    `forceVisible` skips observing and reports true (e.g. for deep-link scroll targets).
 */
export const useInViewport = (
    ref: RefObject<Element | null>,
    { rootSelector, rootMargin, once = false, forceVisible = false }: IUseInViewportOptions = {},
) => {
    const [isIntersecting, setIntersecting] = useState(forceVisible);
    const isSettled = once && isIntersecting;

    useEffect(() => {
        if (forceVisible || isSettled || !ref.current) {
            return;
        }

        const root = rootSelector ? ref.current.closest(rootSelector) : null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!once || entry.isIntersecting) {
                    setIntersecting(entry.isIntersecting);
                }
            },
            { root, rootMargin },
        );
        observer.observe(ref.current);

        return () => {
            observer.disconnect();
        };
    }, [ref, rootSelector, rootMargin, once, forceVisible, isSettled]);

    return isIntersecting || forceVisible;
};
